import { z } from 'zod';
import { AUDIT_SOURCES } from './audit-trail';
import type { ModuleKey } from './modules';
import type { TrafficLight } from './status';

/**
 * Os 14 relatórios do PRD §11.29 (T-201, RF-44, RN-24; DEC-068): os tabulares (T-201a) e os de performance com
 * gráficos (T-201b), na ordem do PRD.
 */
export const REPORT_KEYS = [
  'executivo',
  'performance-bsc',
  'performance-perspectiva',
  'performance-objetivo',
  'kpis',
  'metas',
  'previsto-realizado',
  'iniciativas',
  'planos-acao',
  'acoes-atrasadas',
  'analise-critica',
  'mensal',
  'anual',
  'auditoria',
] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

export const REPORT_FORMATS = ['pdf', 'xlsx', 'csv'] as const;
export type ReportFormat = (typeof REPORT_FORMATS)[number];
export const REPORT_FORMAT_LABELS: Record<ReportFormat, string> = { pdf: 'PDF', xlsx: 'Excel', csv: 'CSV' };
export const REPORT_FORMAT_MIME: Record<ReportFormat, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv; charset=utf-8',
};

/** Filtros que cada relatório aceita (a tela mostra só os do relatório escolhido). */
export const REPORT_FILTERS = [
  'year',
  'period',
  'range',
  'perspective',
  'objective',
  'department',
  'businessUnit',
  'responsible',
  'due',
  'meeting',
  'auditUser',
  'auditEntity',
  'auditSource',
] as const;
export type ReportFilterName = (typeof REPORT_FILTERS)[number];

/** Nome de cada filtro (mensagens e tela). */
export const REPORT_FILTER_LABELS: Record<ReportFilterName, string> = {
  year: 'ano',
  period: 'mês',
  range: 'período',
  perspective: 'perspectiva',
  objective: 'objetivo',
  department: 'área',
  businessUnit: 'unidade',
  responsible: 'responsável',
  due: 'prazo',
  meeting: 'reunião',
  auditUser: 'quem alterou',
  auditEntity: 'entidade',
  auditSource: 'origem',
};

/** Campo da consulta → filtro do catálogo (filtro que o relatório não tem é recusado). */
export const REPORT_QUERY_FILTERS: Record<string, ReportFilterName> = {
  from: 'range',
  to: 'range',
  perspectiveId: 'perspective',
  objectiveId: 'objective',
  departmentId: 'department',
  businessUnitId: 'businessUnit',
  responsibleId: 'responsible',
  dueFrom: 'due',
  dueTo: 'due',
  meetingId: 'meeting',
  userId: 'auditUser',
  entity: 'auditEntity',
  source: 'auditSource',
};

/** Campo da consulta que dá o valor de cada filtro (o período usa o mês; o intervalo e o prazo, o início). */
export const REPORT_FILTER_FIELDS: Record<ReportFilterName, keyof ReportQuery> = {
  year: 'year',
  period: 'month',
  range: 'from',
  perspective: 'perspectiveId',
  objective: 'objectiveId',
  department: 'departmentId',
  businessUnit: 'businessUnitId',
  responsible: 'responsibleId',
  due: 'dueFrom',
  meeting: 'meetingId',
  auditUser: 'userId',
  auditEntity: 'entity',
  auditSource: 'source',
};

const FEMININE_FILTERS: readonly ReportFilterName[] = [
  'perspective',
  'department',
  'businessUnit',
  'meeting',
  'auditEntity',
  'auditSource',
];

/** Pedido do filtro obrigatório que faltou ("Escolha a reunião do relatório."). */
export function reportFilterPrompt(filter: ReportFilterName): string {
  if (filter === 'auditUser') return 'Escolha quem alterou.';
  return `Escolha ${FEMININE_FILTERS.includes(filter) ? 'a' : 'o'} ${REPORT_FILTER_LABELS[filter]} do relatório.`;
}

export interface ReportDefinition {
  key: ReportKey;
  label: string;
  description: string;
  /** Módulo dos dados (precisa "ver"); exportar exige também "exportar" em Relatórios. */
  module: ModuleKey;
  /** Precisa de projeto (todos menos a auditoria, que é da empresa). */
  project: boolean;
  /** CSV só quando o relatório é uma tabela só (PRD §11.29: "CSV, quando aplicável"). */
  formats: readonly ReportFormat[];
  filters: readonly ReportFilterName[];
  /** Filtros obrigatórios (o relatório de um objetivo precisa do objetivo; o da análise crítica, da reunião). */
  required?: readonly ReportFilterName[];
}

const ALL_FORMATS = ['pdf', 'xlsx', 'csv'] as const;
/** Relatório com gráficos e várias tabelas: PDF e Excel (o CSV é para uma tabela só). */
const NO_CSV = ['pdf', 'xlsx'] as const;

export const REPORTS: readonly ReportDefinition[] = [
  {
    key: 'executivo',
    label: 'Executivo',
    description:
      'Performance da empresa e das perspectivas no mês, a evolução no ano, objetivos e KPIs críticos, ações atrasadas e iniciativas em risco.',
    module: 'dashboard-executivo',
    project: true,
    formats: NO_CSV,
    filters: ['period'],
  },
  {
    key: 'performance-bsc',
    label: 'Performance BSC',
    description: 'Scorecard do mês: empresa, perspectivas, objetivos e KPIs, no mês e no acumulado do ano.',
    module: 'metas-resultados',
    project: true,
    formats: NO_CSV,
    filters: ['period'],
  },
  {
    key: 'performance-perspectiva',
    label: 'Performance por perspectiva',
    description:
      'Cada perspectiva com a evolução no ano, os objetivos com o peso, os KPIs e os desvios do mês.',
    module: 'dashboard-perspectiva',
    project: true,
    formats: NO_CSV,
    filters: ['period', 'perspective'],
  },
  {
    key: 'performance-objetivo',
    label: 'Performance por objetivo',
    description: 'O objetivo no mês: performance, evolução no ano, KPIs, desvios e iniciativas.',
    module: 'objetivos',
    project: true,
    formats: NO_CSV,
    filters: ['period', 'objective'],
    required: ['objective'],
  },
  {
    key: 'kpis',
    label: 'KPIs',
    description:
      'Cadastro dos indicadores: código, perspectiva, área, responsável, unidade, periodicidade, fonte e objetivos.',
    module: 'indicadores',
    project: true,
    formats: ALL_FORMATS,
    filters: ['perspective', 'objective', 'department'],
  },
  {
    key: 'metas',
    label: 'Metas',
    description: 'Metas do ano por KPI e mês, com o total do ano e a situação da aprovação.',
    module: 'metas-resultados',
    project: true,
    formats: ALL_FORMATS,
    filters: ['year', 'department', 'businessUnit'],
  },
  {
    key: 'previsto-realizado',
    label: 'Previsto x realizado',
    description:
      'Meta, realizado, diferença e performance de cada KPI no mês e no acumulado do ano, do consolidado ou de uma unidade.',
    module: 'previsto-realizado',
    project: true,
    formats: ALL_FORMATS,
    filters: ['period', 'perspective', 'objective', 'department', 'businessUnit'],
  },
  {
    key: 'iniciativas',
    label: 'Iniciativas',
    description:
      'Iniciativas com objetivo, líder, prazo, progresso, situação, riscos e orçamento previsto × realizado.',
    module: 'iniciativas',
    project: true,
    formats: ALL_FORMATS,
    filters: ['perspective', 'objective'],
  },
  {
    key: 'planos-acao',
    label: 'Planos de ação',
    description: 'Ações (5W2H) dos planos ativos com o plano, a origem, o responsável, o prazo e a situação.',
    module: 'planos-acao',
    project: true,
    formats: ALL_FORMATS,
    filters: ['objective', 'responsible', 'due'],
  },
  {
    key: 'acoes-atrasadas',
    label: 'Ações atrasadas',
    description: 'Ações com o prazo vencido e não concluídas, com os dias de atraso e o responsável.',
    module: 'planos-acao',
    project: true,
    formats: ALL_FORMATS,
    filters: ['responsible', 'due'],
  },
  {
    key: 'analise-critica',
    label: 'Análise crítica',
    description:
      'A análise crítica encerrada: participantes, cada item com o retrato na pauta e no encerramento, a justificativa e as decisões.',
    module: 'reunioes-decisoes',
    project: true,
    formats: NO_CSV,
    filters: ['meeting'],
    required: ['meeting'],
  },
  {
    key: 'mensal',
    label: 'Mensal',
    description:
      'O fechamento do mês: performance, números do mês, objetivos e KPIs críticos, ações atrasadas, metas no limite, riscos e decisões pendentes.',
    module: 'visao-geral',
    project: true,
    formats: NO_CSV,
    filters: ['period'],
  },
  {
    key: 'anual',
    label: 'Anual',
    description: 'O ano mês a mês: a performance da empresa, das perspectivas e dos KPIs, com o acumulado.',
    module: 'metas-resultados',
    project: true,
    formats: NO_CSV,
    filters: ['year'],
  },
  {
    key: 'auditoria',
    label: 'Auditoria',
    description: 'Trilha de alterações com anterior → novo, quem, quando, a origem e a justificativa.',
    module: 'auditoria',
    project: false,
    formats: ALL_FORMATS,
    filters: ['range', 'auditUser', 'auditEntity', 'auditSource'],
  },
];

export const reportOf = (key: ReportKey): ReportDefinition => REPORTS.find((r) => r.key === key)!;

/** Documento genérico que cada relatório monta e os três formatos desenham. */
export type ReportCell = string | number | null;
export type ReportColumnType = 'text' | 'number' | 'percent' | 'money' | 'date';
export interface ReportColumn {
  key: string;
  label: string;
  type?: ReportColumnType;
}
export interface ReportTable {
  kind: 'table';
  title: string;
  columns: ReportColumn[];
  rows: Record<string, ReportCell>[];
  /** Aviso quando a consulta tem mais linhas do que o relatório mostra. */
  note?: string;
}
export interface ReportFacts {
  kind: 'facts';
  title: string;
  items: { label: string; value: string }[];
}
/** Gráfico do PDF (SVG); no Excel vira a tabela dos dados. */
export interface ReportChart {
  kind: 'chart';
  title: string;
  /** Barras (com a cor do semáforo de cada ponto) ou linhas (evolução). */
  type: 'bar' | 'line';
  /** "%" para performance. */
  unit: '%' | null;
  /** Linha de referência (100% da meta). */
  reference?: number;
  series: { label: string; points: { label: string; value: number | null; light?: TrafficLight }[] }[];
}
export interface ReportText {
  kind: 'text';
  title: string;
  paragraphs: string[];
}
export type ReportSection = ReportTable | ReportFacts | ReportChart | ReportText;
export interface ReportDocument {
  key: ReportKey;
  title: string;
  /** Empresa, projeto e os filtros como a pessoa lê ("Projeto: BSC 2026 · Área: Comercial"). */
  subtitle: string;
  organizationName: string;
  generatedAt: string;
  sections: ReportSection[];
}

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida.');
const id = z.string().uuid();

export const reportQuerySchema = z
  .object({
    format: z.enum(REPORT_FORMATS),
    projectId: id.optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
    from: isoDay.optional(),
    to: isoDay.optional(),
    perspectiveId: id.optional(),
    objectiveId: id.optional(),
    departmentId: id.optional(),
    businessUnitId: id.optional(),
    responsibleId: id.optional(),
    dueFrom: isoDay.optional(),
    dueTo: isoDay.optional(),
    meetingId: id.optional(),
    userId: id.optional(),
    entity: z
      .string()
      .regex(/^[a-z_]+$/, 'Entidade inválida.')
      .optional(),
    source: z.enum(AUDIT_SOURCES).optional(),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'O fim vem depois do início.',
    path: ['to'],
  })
  .refine((q) => !q.dueFrom || !q.dueTo || q.dueFrom <= q.dueTo, {
    message: 'O fim vem depois do início.',
    path: ['dueTo'],
  })
  .refine((q) => q.month === undefined || q.year !== undefined, {
    message: 'Informe o ano junto com o mês.',
    path: ['year'],
  });
export type ReportQuery = z.infer<typeof reportQuerySchema>;

/** Nome do arquivo baixado: "kpis-2026-10-28.pdf". */
export const reportFileName = (key: ReportKey, format: ReportFormat, day: string) =>
  `${key}-${day}.${format}`;

// Relatórios grandes gerados na fila (T-201c; DEC-068).
export const REPORT_EXPORT_STATUSES = ['QUEUED', 'PROCESSING', 'DONE', 'FAILED'] as const;
export type ReportExportStatusName = (typeof REPORT_EXPORT_STATUSES)[number];
export const REPORT_EXPORT_STATUS_LABELS: Record<ReportExportStatusName, string> = {
  QUEUED: 'Na fila',
  PROCESSING: 'Gerando',
  DONE: 'Pronto para baixar',
  FAILED: 'Não foi possível gerar',
};

export const reportExportSchema = z.object({
  id: z.string().uuid(),
  reportKey: z.enum(REPORT_KEYS),
  /** Nome do relatório e os filtros como a pessoa lê. */
  label: z.string(),
  subtitle: z.string(),
  format: z.enum(REPORT_FORMATS),
  status: z.enum(REPORT_EXPORT_STATUSES),
  fileName: z.string().nullable(),
  size: z.number().int().nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  /** Até quando o arquivo pode ser baixado. */
  expiresAt: z.string().nullable(),
});
export type ReportExport = z.infer<typeof reportExportSchema>;

export const reportExportListSchema = z.object({ items: z.array(reportExportSchema) });

/** Resposta da exportação quando o relatório passou do limite da hora: foi para a fila. */
export const reportQueuedSchema = z.object({
  queued: z.literal(true),
  export: reportExportSchema,
  message: z.string(),
});
